import { useMemo } from "react"

import { NeatPath } from "@/models/route"

import { useGetScheduleMisalignment } from "../data/use-get-schedule-misalignment"
import type { ScheduleMisalignmentDetail } from "../pages/schedule-misalignment-route/schedule-misalignment.types"
import type { ActionRow } from "./actions.types"

/** Map the E2 detail's per-site rows to the common ActionRow shape. */
export const e2ToActionRows = (
  detail: ScheduleMisalignmentDetail | undefined
): ActionRow[] =>
  (detail?.sites ?? []).map((s) => ({
    id: s.action_id,
    site_id: s.site_id,
    site_label: s.site_label,
    detector_code: "E2",
    detector_label: "ScheduleMisalignment",
    detector_path: NeatPath.ScheduleMisalignment,
    action_text: s.action_text,
    impact_dollars: s.impact_dollars,
    confidence: s.confidence,
  }))

// C1 is deliberately NOT a row here. At $869 on one site against E2's $78k it
// never earned a card, and a third to three-quarters of its kWh was the same
// after-hours overspend seen from another angle — two cards, one problem, one
// hero sum counting it twice. It lives on E2's leaf as a scenario ladder and
// has no standalone route: one was built and then removed before merge, because
// nothing linked to it and the ladder already said everything it said.

/**
 * Fan out over every ACT detector detail hook, map to ActionRow, concatenate,
 * and rank by observed impact desc. Adding a detector = one more hook call +
 * mapper spread below (hooks are called unconditionally per React's rules —
 * the detector set is a fixed, known list).
 */
export const useActionRows = (): { rows: ActionRow[]; isPending: boolean } => {
  const e2 = useGetScheduleMisalignment()

  const rows = useMemo(
    () =>
      [...e2ToActionRows(e2.data)].sort(
        (a, b) => b.impact_dollars - a.impact_dollars
      ),
    [e2.data]
  )

  return { rows, isPending: e2.isPending }
}
